import {observer} from 'mobx-react';
import {useCallback, useEffect, useRef} from 'react';
import {Frame} from '../common/Frame';
import {VideoPlayerState} from './useVideoPlayerState';
import {VideoPlayerControls} from './VideoPlayerControls';
import {VideoPlayerTimeline} from './VideoPlayerTimeline';
import {recording} from '../../proto/compiled';
import {useMobx} from '../../hooks/useMobx';
import {D} from '../../Constants';

export type VideoSizing = 'default' | 'cover' | 'contain';

export interface VideoPlayerProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  src: string;
  annotations?: recording.IRecordingAnnotation[];
  selectedAnnotation?: recording.IRecordingAnnotation;
  onAnnotationSelect?: (annotation: recording.IRecordingAnnotation | undefined) => void;
  editMode?: boolean;
  onAnnotationBoundsChange?: (annotation: recording.IRecordingAnnotation, startMs: number, endMs: number) => void;
  limitToAnnotation?: recording.IRecordingAnnotation;
  onTimeUpdate?: (currentTimeMs: number) => void;
  onDurationChange?: (durationMs: number) => void;
  onAspectRatioChange?: (aspectRatio: number) => void;
  aspectRatio?: number;
  initialTimeMs?: number;
  sizing?: VideoSizing;
}

export const VideoPlayer = observer(({
  videoRef,
  src,
  annotations,
  selectedAnnotation,
  onAnnotationSelect,
  editMode,
  onAnnotationBoundsChange,
  limitToAnnotation,
  onTimeUpdate,
  onDurationChange,
  onAspectRatioChange,
  aspectRatio,
  initialTimeMs,
  sizing = 'default',
}: VideoPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wasPlayingBeforeScrub = useRef(false);
  const lastProgressMs = useRef(0);
  const state = useMobx(() => new VideoPlayerState());

  // Fallback time update for when RAF isn't running (paused/seeking)
  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;
    const currentMs = Math.round(videoRef.current.currentTime * 1000);
    state.setCurrentTime(currentMs);
    onTimeUpdate?.(currentMs);
  }, [videoRef, state, onTimeUpdate]);

  const handleLoadedMetadata = useCallback(() => {
    if (!videoRef.current) return;
    const durationMs = Math.round(videoRef.current.duration * 1000);
    state.setDuration(durationMs);
    onDurationChange?.(durationMs);

    // Report aspect ratio
    const videoWidth = videoRef.current.videoWidth;
    const videoHeight = videoRef.current.videoHeight;
    if (videoWidth > 0 && videoHeight > 0) {
      onAspectRatioChange?.(videoWidth / videoHeight);
    }

    // Seek to initial time if provided
    if (initialTimeMs !== undefined && initialTimeMs > 0) {
      videoRef.current.currentTime = initialTimeMs / 1000;
      state.setCurrentTime(initialTimeMs);
      lastProgressMs.current = initialTimeMs;
    }
  }, [videoRef, state, onDurationChange, onAspectRatioChange, initialTimeMs]);

  const handlePlay = useCallback(() => {
    state.setPlaying(true);
  }, [state]);

  const handlePause = useCallback(() => {
    state.setPlaying(false);
  }, [state]);

  const handleSeek = useCallback((timeMs: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = timeMs / 1000;
    state.setCurrentTime(timeMs);
    onTimeUpdate?.(timeMs);
  }, [videoRef, state, onTimeUpdate]);

  const handlePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    if (state.playing) {
      videoRef.current.pause();
    } else {
      const currentMs = state.currentTimeMs;
      const annotationStart = limitToAnnotation?.startMs ?? initialTimeMs ?? 0;

      // If exactly at annotation end, restart from annotation start
      if (limitToAnnotation && limitToAnnotation.endMs && currentMs === limitToAnnotation.endMs) {
        videoRef.current.currentTime = annotationStart / 1000;
      }
      // If at video end (natural end), restart from annotation start
      else if (state.durationMs > 0 && currentMs >= state.durationMs - 100) {
        videoRef.current.currentTime = annotationStart / 1000;
      }

      videoRef.current.play();
    }
  }, [videoRef, state, limitToAnnotation, initialTimeMs]);

  const handleReplay = useCallback(() => {
    if (!videoRef.current) return;
    const annotationStart = limitToAnnotation?.startMs ?? initialTimeMs ?? 0;
    videoRef.current.currentTime = annotationStart / 1000;
    state.setCurrentTime(annotationStart);
    lastProgressMs.current = annotationStart;
    onTimeUpdate?.(annotationStart);
    videoRef.current.play();
  }, [videoRef, state, limitToAnnotation, initialTimeMs, onTimeUpdate]);

  const handleVolumeChange = useCallback((volume: number) => {
    if (!videoRef.current) return;
    videoRef.current.volume = volume;
    state.setVolume(volume);
    // Unmute when adjusting volume
    if (state.muted && volume > 0) {
      videoRef.current.muted = false;
      state.setMuted(false);
    }
  }, [videoRef, state]);

  const handleMuteToggle = useCallback(() => {
    if (!videoRef.current) return;
    const newMuted = !state.muted;
    videoRef.current.muted = newMuted;
    state.setMuted(newMuted);
  }, [videoRef, state]);

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  }, []);

  const handleAnnotationSelect = useCallback((annotation: recording.IRecordingAnnotation | undefined) => {
    // Check if we just finished dragging or are still dragging (don't auto-play in those cases)
    const wasDragging = state.wasDraggingHandle;
    const isDragging = state.isDraggingHandle || state.isDraggingTimeline;
    const wasAlreadySelected = annotation === selectedAnnotation;
    const wasPaused = videoRef.current?.paused ?? true;
    state.clearWasDragging();

    onAnnotationSelect?.(annotation);
    // Don't auto-play if dragging, or if already selected and was paused
    if (annotation && videoRef.current && !wasDragging && !isDragging && !(wasAlreadySelected && wasPaused)) {
      videoRef.current.play();
    }
  }, [onAnnotationSelect, videoRef, state, selectedAnnotation]);

  const handleAnnotationBoundsChange = useCallback((annotation: recording.IRecordingAnnotation, startMs: number, endMs: number) => {
    onAnnotationBoundsChange?.(annotation, startMs, endMs);
  }, [onAnnotationBoundsChange]);

  const handleScrubStart = useCallback(() => {
    if (!videoRef.current) return;
    wasPlayingBeforeScrub.current = !videoRef.current.paused;
    videoRef.current.pause();
  }, [videoRef]);

  const handleScrubEnd = useCallback(() => {
    if (!videoRef.current) return;
    if (wasPlayingBeforeScrub.current) {
      videoRef.current.play();
    }
  }, [videoRef]);

  // Sync video element state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.volume = state.volume;
    video.muted = state.muted;
  }, [videoRef, state.volume, state.muted]);

  // High-frequency progress updates using requestAnimationFrame
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let animationFrameId: number;

    const updateProgress = () => {
      if (video && !video.paused) {
        const currentMs = Math.round(video.currentTime * 1000);
        state.setCurrentTime(currentMs);
        onTimeUpdate?.(currentMs);

        // Handle limitToAnnotation - stop at end (only if we crossed the boundary)
        if (limitToAnnotation && limitToAnnotation.endMs && limitToAnnotation.endMs > 0) {
          // Only stop if we crossed from before end to at/after end
          if (lastProgressMs.current < limitToAnnotation.endMs && currentMs >= limitToAnnotation.endMs) {
            video.pause();
            video.currentTime = limitToAnnotation.endMs / 1000;
            state.setCurrentTime(limitToAnnotation.endMs);
            state.setPlaying(false);
            onTimeUpdate?.(limitToAnnotation.endMs);
          }
        }
        lastProgressMs.current = currentMs;
      }
      animationFrameId = requestAnimationFrame(updateProgress);
    };

    animationFrameId = requestAnimationFrame(updateProgress);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [videoRef, state, onTimeUpdate, limitToAnnotation]);

  // Only 'cover' overlays the controls on the video; 'contain' stacks them
  // below so the timeline never covers the picture.
  const isOverlay = sizing === 'cover';
  const objectFit = sizing === 'cover' ? 'cover' : 'contain';

  const controlsElement = (
    <Frame
      direction="column"
      paddingH={1.5}
      gap={1}
      onClick={(e) => e.stopPropagation()}
      style={{
        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
        // stacked mode: no space above the track - the timeline is pulled up
        // by half its hit-box height so the track line sits exactly on the
        // video's bottom border (handles overlap the picture slightly)
        paddingTop: isOverlay ? D.unit(1.5) : 0,
        paddingBottom: D.unit(0.75),
        ...(isOverlay ? {
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
        } : {}),
      }}
    >
      <Frame style={isOverlay ? undefined : {marginTop: -12}}>
        <VideoPlayerTimeline
          state={state}
          annotations={annotations}
          selectedAnnotation={selectedAnnotation}
          editMode={editMode}
          onSeek={handleSeek}
          onAnnotationSelect={handleAnnotationSelect}
          onAnnotationBoundsChange={handleAnnotationBoundsChange}
          onScrubStart={handleScrubStart}
          onScrubEnd={handleScrubEnd}
        />
      </Frame>
      <VideoPlayerControls
        state={state}
        onPlayPause={handlePlayPause}
        onReplay={initialTimeMs !== undefined ? handleReplay : undefined}
        onVolumeChange={handleVolumeChange}
        onMuteToggle={handleMuteToggle}
        onFullscreen={handleFullscreen}
        selectedAnnotationTitle={selectedAnnotation?.label || undefined}
        onAnnotationClose={onAnnotationSelect ? () => onAnnotationSelect(undefined) : undefined}
      />
    </Frame>
  );

  if (isOverlay) {
    // Cover/Contain mode: video fills container, controls overlay
    return (
      <Frame
        ref={containerRef}
        grow
        clickable
        onClick={handlePlayPause}
        style={{
          backgroundColor: '#000',
          borderRadius: 16,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <video
          ref={videoRef}
          src={src}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          style={{
            width: '100%',
            height: '100%',
            objectFit,
          }}
        />
        {controlsElement}
      </Frame>
    );
  }

  // Default mode: video on top, controls below
  return (
    <Frame
      ref={containerRef}
      direction="column"
      grow
      style={{
        backgroundColor: '#000',
        borderRadius: 16,
        overflow: 'hidden',
        // basis auto so the AR-derived intrinsic height propagates to the
        // parent (grow's basis 0 collapsed the card to its text height)
        ...(aspectRatio ? {flex: '1 1 auto'} : {}),
      }}
    >
      {/* Video Container - keeps the video's aspect ratio when known so the
          stacked controls add height below instead of eating into the frame */}
      <Frame
        grow
        center
        clickable
        onClick={handlePlayPause}
        style={{
          minHeight: 0,
          position: 'relative',
          // flex-basis auto keeps the AR-derived height (Frame's grow sets
          // basis 0, which collapses the video to leftover space); shrink
          // stays on so fullscreen can still letterbox
          ...(aspectRatio ? {aspectRatio: `${aspectRatio}`, flex: '0 1 auto'} : {}),
        }}
      >
        <video
          ref={videoRef}
          src={src}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          style={{
            width: '100%',
            height: '100%',
            objectFit,
          }}
        />
      </Frame>
      {controlsElement}
    </Frame>
  );
});
